import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Snackbar,
  Dialog,
  DialogContent,
  Tooltip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import PropTypes from "prop-types";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import CustomModal from "shared/components/MDmodal/CustomModal";
import FormTipoActividad from "../Form/FormTipoActividad";
import {
  getAllActivityType,
  updateActivityTypeService,
  deleteActivityTypeService,
} from "modules/bitacora/services/Configuracion/TipoActividad/tipoActividadService";

function TablaTipoActividad({ refreshFlag }) {
  const [activityTypes, setActivityTypes] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [activityTypeSelect, setActivityTypeSelect] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [activityTypeToDelete, setActivityTypeToDelete] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = useCallback((message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const fetchActivityType = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllActivityType();
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      setActivityTypes(parsedData || []);
    } catch (err) {
      console.error("❌ Error al obtener zonas:", err);
      showNotification("Error al cargar zonas", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchActivityType();
  }, [refreshFlag]);

  const handleEditar = useCallback((activityType) => {
    setActivityTypeSelect(activityType);
    setOpenModal(true);
  }, []);

  const handleEliminarClick = useCallback((id) => {
    setActivityTypeToDelete(id);
    setOpenDeleteDialog(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!activityTypeToDelete) return;
    try {
      setLoading(true);
      await deleteActivityTypeService(activityTypeToDelete);
      await fetchActivityType();
      showNotification("Tipo de actividad eliminada correctamente", "success");
    } catch (err) {
      console.error("Error al eliminar el tipo de actividad:", err);
      const errorMessage = err.message?.includes("token")
        ? "Su sesión ha expirado. Por favor inicie sesión nuevamente."
        : "Error al eliminar el tipo de actividad";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setActivityTypeToDelete(null);
    }
  }, [activityTypeToDelete, fetchActivityType, showNotification]);

  const handleActualizar = useCallback(
    async (datos) => {
      if (!activityTypeSelect) return;
      try {
        setLoading(true);
        await updateActivityTypeService(activityTypeSelect.id, {
          title: datos.title,
          description: datos.description,
          status: datos.status,
        });
        await fetchActivityType();
        setOpenModal(false);
        showNotification("Tipo de actividad actualizada correctamente", "success");
      } catch (err) {
        console.error("Error al actualizar el tipo de actividad:", err);
        const message = err.response?.data?.message || "Error al actualizar el tipo de actividad";
        showNotification(message, "error");
      } finally {
        setLoading(false);
      }
    },
    [activityTypeSelect, fetchActivityType, showNotification]
  );

  const columns = [
    { Header: "Nombre", accessor: "title", width: "30%" },
    { Header: "Descripción", accessor: "description", width: "30%" },
    { Header: "Estado", accessor: "status", width: "40%" },
    { Header: "Acciones", accessor: "acciones", align: "center", width: "10%" },
  ];

  const rows = activityTypes.map((activityType) => ({
    title: (
      <MDTypography variant="button" fontWeight="medium">
        {activityType.title || "Sin nombre"}
      </MDTypography>
    ),
    description: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {activityType.description || "Sin descripción"}
      </MDTypography>
    ),
    status: (
      <MDTypography
        variant="caption"
        color={activityType.status === true ? "success" : "error"}
        fontWeight="medium"
      >
        {activityType.status === true ? "Activo" : "Inactivo"}
      </MDTypography>
    ),
    acciones: (
      <MDBox display="flex" justifyContent="center">
        <Tooltip title="Editar Tipo de Actividad">
          <IconButton color="info" onClick={() => handleEditar(activityType)}>
            <Edit />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar Tipo de Actividad">
          <IconButton color="error" onClick={() => handleEliminarClick(activityType.id)}>
            <Delete />
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  return (
    <>
      <MDBox
        mx={-1}
        mt={-1}
        py={2}
        px={2}
        variant="gradient"
        bgColor="success"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          Gestión de Tipos de Actividad
        </MDTypography>
      </MDBox>

      <MDBox pt={1} px={-2}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress color="info" />
          </MDBox>
        ) : error ? (
          <MDBox p={2}>
            <Alert severity="error">{error}</Alert>
          </MDBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            isSorted
            entriesPerPage={false}
            showTotalEntries
            noEndBorder
          />
        )}
      </MDBox>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDeleteDialog}
        onClose={() => !loading && setOpenDeleteDialog(false)}
        PaperProps={{
          sx: { minWidth: "400px", borderRadius: "12px", padding: "16px" },
        }}
      >
        <MDBox display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: "error.light", mr: 2 }}>
            <Delete color="error" />
          </Avatar>
          <MDTypography variant="h6" fontWeight="medium">
            Confirmar eliminación
          </MDTypography>
        </MDBox>

        <DialogContent>
          <MDTypography variant="body2">
            Esta acción eliminará el tipo de actividad del sistema.
          </MDTypography>
          <MDTypography variant="body2" fontWeight="bold" mt={1}>
            ¿Deseas continuar?
          </MDTypography>
        </DialogContent>

        <MDBox display="flex" justifyContent="space-between" px={3} pb={2}>
          <MDButton
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            color="error"
            disabled={loading}
            sx={{ minWidth: "120px" }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={confirmDelete}
            color="error"
            variant="gradient"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ minWidth: "120px" }}
          >
            {loading ? "Eliminando..." : "Confirmar"}
          </MDButton>
        </MDBox>
      </Dialog>

      <CustomModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setError(null);
        }}
        title={`Editar Tipo de actividad - ${activityTypeSelect?.title || ""}`}
      >
        {activityTypeSelect && (
          <FormTipoActividad
            initialData={{
              title: activityTypeSelect?.title || "",
              description: activityTypeSelect?.description || "",
              status: activityTypeSelect?.status || true,
            }}
            onSubmit={handleActualizar}
            isEditMode
            loading={loading}
          />
        )}
      </CustomModal>
    </>
  );
}

TablaTipoActividad.propTypes = {
  refreshFlag: PropTypes.bool,
};

export default TablaTipoActividad;
